<template>
  <div
    class="base-input base-input__outer"
    :class="[
      className,
      {
        disabled,
        error: error && !focused,
        'error-counter': hasCounterError,
        focused,
      },
    ]"
  >
    <div class="base-input__wrapper">
      <slot
        :id="inputId"
        name="label"
        :value="modelValue"
      >
        <label
          v-if="label"
          class="base-input__label"
          :for="inputId"
        >
          {{ label }}
        </label>
      </slot>

      <div class="base-input__inner">
        <slot
          :id="inputId"
          name="prefix"
          :value="modelValue"
        />

        <VueTelInput
          :id="inputId"
          ref="phoneInputElement"
          v-model="modelValue"
          class="base-input__element"
          :default-country
          v-bind="attrs"
          :disabled
          :dropdown-options="{
            showSearchBox: true,
            showFlags: true,
            showDialCodeInList: true,
          }"
          :input-options="{
            showDialCode: true,
            placeholder: placeholder ?? '',
            inputmode: 'numeric',
            type: 'tel',
          }"
          mode="international"
          :name
          :valid-characters-only="true"
          @focusin="onFocusIn"
          @focusout="onFocusOut"
          @keydown="onKeyDown"
          @update="onUpdate"
        />

        <slot
          :id="inputId"
          name="suffix"
          :value="modelValue"
        />
      </div>

      <slot
        :id="inputId"
        name="counter"
        :number-characters
        :value="modelValue"
      >
        <span
          v-if="counter"
          class="base-input__counter"
          :class="[{ error: hasCounterError }]"
        >
          {{ numberCharacters }}/{{ counter }}
        </span>
      </slot>
    </div>

    <div
      v-if="((error || slots.error) && !focused) || hint || slots.hint"
      class="base-input__messages"
    >
      <slot
        v-if="error"
        :id="inputId"
        name="error"
        :value="modelValue"
      >
        <p
          v-if="error && !focused"
          class="base-input__error"
        >
          {{ error }}
        </p>
      </slot>

      <slot
        :id="inputId"
        name="hint"
        :value="modelValue"
      >
        <p
          v-if="hint"
          class="base-input__hint"
        >
          {{ hint }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { VueTelInput } from "vue-tel-input";

import "vue-tel-input/vue-tel-input.css";

export interface Props {
  allowedCountries?: string[];
  class?: string;
  counter?: string;
  defaultCountry?: string;
  disabled?: boolean;
  error?: boolean | string;
  hint?: string;
  id?: string;
  label?: string;
  name: string;
  placeholder?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  submit: [];
}>();

const modelValue = defineModel<string>();
const slots = useSlots();
const attrs = useAttrs();
const { class: className, counter, id, name } = toRefs(props);
const phoneInputElement = ref<any>(null);
const inputId = id.value ?? name.value;
const focused = ref(false);
const numberCharacters = computed(() => {
  return modelValue.value?.length ?? 0;
});
const hasCounterError = computed(() => {
  return numberCharacters.value > Number(counter.value);
});

const onFocusIn = () => {
  focused.value = true;
  // Получаем реальный input элемент из VueTelInput
  const inputElement = phoneInputElement.value?.$el?.querySelector("input");
};

const onFocusOut = () => {
  focused.value = false;
  const inputElement = phoneInputElement.value?.$el?.querySelector("input");
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    event.preventDefault();
    const inputElement = phoneInputElement.value?.$el?.querySelector("input");
    inputElement?.blur();
    emit("submit");
  }

  if (event.key === "Escape") {
    const inputElement = phoneInputElement.value?.$el?.querySelector("input");
    inputElement?.blur();
  }
};

const onUpdate = (value) => {
  modelValue.value = value;
};

onMounted(() => {
  // Добавляем обработчик клика вне элемента для VueTelInput
  const inputElement = phoneInputElement.value?.$el?.querySelector("input");
  if (inputElement) {
    onClickOutside(phoneInputElement.value.$el, () => {
      if (focused.value) {
        inputElement.blur();
      }
    });
  }
});
</script>

<style scoped>
.base-input__outer {
  display: flex;
  flex-direction: column;
}

.vti__dropdown {
  padding: 4px 2px;
}

.vue-tel-input:focus-within {
  box-shadow: none;
}

.base-input__wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.base-input__inner {
  display: flex;
  width: 100%;
}

.base-input__label {
  display: flex;
}

.base-input__element {
  width: 100%;
  line-height: 1.43;
  padding: 3px 0;
  border: 0;
  border-radius: 4px;
  font-size: 14px;
}

.base-input__messages {
  display: flex;
  flex-direction: column;
}

.base-input-first .base-input__messages {
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: 0.1px;
}

.base-input-first .base-input__inner {
  padding: 0px;
}

.base-input-first .base-input__error {
  color: var(--red);
}

.base-input-first.base-input.error .base-input__inner {
  border-color: var(--red);
}

.base-input-first.base-input.focused .base-input__inner {
  border-color: var(--grey-5);
}
</style>
